import _, { isArray } from "lodash";
import { Button } from "primereact/button";
import { Menubar } from "primereact/menubar";
import { type MenuItem, type MenuItemOptions } from "primereact/menuitem";
import { Suspense, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import type { PreloadedQuery } from "react-relay";
import { usePreloadedQuery, useQueryLoader } from "react-relay";
import { BrowserRouter, Navigate, NavLink, Route, Routes } from "react-router-dom";
import { AccountSwitcher } from "@components/account-switcher/account-switcher.component";
import { EnvironmentBadge } from "@components/environment-badge";
import { GenerateAppJwt } from "@components/generate-app-jwt/generate-app-jwt.component";
import { LanguageSwitcher } from "@components/language-switcher/language-switcher.component";
import { Loader } from "@components/Loader";
import { useAuthContext } from "@hooks/use-auth-context";
import { useTranslation } from "@i18n/hooks";
import type {
	Permission,
	permissionBasedNavigation_Query,
} from "@relay/permissionBasedNavigation_Query.graphql";
import { Paths } from "@routes/paths";
import { type RouteDefinition } from "@routes/route-definition";
import { AppRoutes } from "@routes/routes";
import { HomeScreen } from "@screens/home";
import { LoginScreen } from "@screens/login/login.screen";
import { selectAppJwt, selectHasAppJwt } from "@store/slices/AuthSlice";
import { type CurrentUserData, setCurrentUser } from "@store/slices/CurrentUserSlice";
import { useTypedSelector } from "@store/store.redux";
import { PermissionBasedNavigationFeatureToggles } from "./permission-based-navigation-feature-toggles.component";
import { PERMISSION_QUERY } from "./permission-based-navigation.graphql";
import { appEnvironment, hasMobileApp } from "../environment";

type PermissionBasedNavigationProps = {
	queryRef: PreloadedQuery<permissionBasedNavigation_Query>;
	onPermissionsChange?: (key: string) => void;
};

function PermissionBasedNavigationWithPreloadedQuery({
	queryRef,
	onPermissionsChange,
}: PermissionBasedNavigationProps) {
	const { t } = useTranslation("common");
	const query = usePreloadedQuery<permissionBasedNavigation_Query>(PERMISSION_QUERY, queryRef);
	const dispatch = useDispatch();
	const { logout } = useAuthContext();
	const hasAppJwt = useTypedSelector(selectHasAppJwt);
	const isLoggedIn = !!query.Viewer.Auth.currentUser;

	const permissionsFromQuery = query.Viewer.Auth.currentUser?.permissionsInAccount || [];
	const permissionsKey = JSON.stringify(permissionsFromQuery);

	useEffect(() => {
		const { currentUser } = query.Viewer.Auth;
		if (currentUser) {
			dispatch(setCurrentUser(currentUser as CurrentUserData));
			const permissions = currentUser.permissionsInAccount || [];
			if (permissions.length > 0) {
				onPermissionsChange?.(permissionsKey);
			}
		}
	}, [query.Viewer.Auth.currentUser, dispatch, permissionsKey, onPermissionsChange]);

	const routes = [
		...AppRoutes,
		{
			path: "/",
			element: <HomeScreen />,
			requiredPermissions: "logged-in",
		} satisfies RouteDefinition,
		{
			path: "*",
			element: <LoginScreen />,
			requiredPermissions: "only-logged-out",
		} satisfies RouteDefinition,
	];
	const MenuTemplate = (item: MenuItem, options: MenuItemOptions) => (
		<NavLink className={options.className} target={item.target} to={item.url!}>
			{item.label}
		</NavLink>
	);

	const isOwnerOfRoot =
		permissionsFromQuery.includes("UserInAccountPermission_System_Owner") &&
		permissionsFromQuery.includes("AccountPermission_System_Root");

	const checkPermissionsLocal = (necessaryPermissions: Permission[] | "onlyOwnerOfRoot") => {
		if (necessaryPermissions === "onlyOwnerOfRoot") return isOwnerOfRoot;
		return (
			isOwnerOfRoot ||
			necessaryPermissions.every((permission) => permissionsFromQuery.includes(permission))
		);
	};

	const buildMenuItems = () => {
		const menuItems: MenuItem[] = [
			{
				label: t("navigation.home"),
				url: "/",
				template: MenuTemplate,
			},
		];

		if (checkPermissionsLocal(["UserInAccountPermission_Nodes_ReadNodes"])) {
			menuItems.push({
				label: t("navigation.offers"),
				items: [
					{
						label: t("navigation.myOffers"),
						url: Paths.offers.path,
						template: MenuTemplate,
					},
					{
						label: t("navigation.offerCatalogue"),
						url: Paths.offerCatalogue.path,
						template: MenuTemplate,
					},
					{
						label: t("navigation.offerSubscriptions"),
						url: Paths.offerSubscriptions.path,
						template: MenuTemplate,
					},
				],
			});
		}

		const canViewProducts = checkPermissionsLocal([
			"UserInAccountPermission_ProductAdmin_Read",
		]);
		const canViewOrders = checkPermissionsLocal(["UserInAccountPermission_OrderAdmin_Read"]);
		const canViewDiscounts = checkPermissionsLocal([
			"UserInAccountPermission_DiscountAdmin_Read",
		]);
		const canViewGenerateCartLink = checkPermissionsLocal([
			"UserInAccountPermission_GenerateCartLinkAdmin_Modify",
		]);
		const canViewLimitedCartDiscounts = checkPermissionsLocal([
			"UserInAccountPermission_LimitedCartDiscountAdmin_Read",
		]);
		const canViewLicenses = checkPermissionsLocal([
			"UserInAccountPermission_LicenseAdmin_Read",
		]);
		const canViewExternalLicenses = checkPermissionsLocal([
			"UserInAccountPermission_ExternalLicenseAdmin_Read",
		]);
		const canViewLevelDefinitions = checkPermissionsLocal([
			"UserInAccountPermission_LevelDefinitionAdmin_Read",
		]);

		const canViewVideoSources = checkPermissionsLocal([
			"UserInAccountPermission_VimeoVideoSourceAdmin_Read",
		]);

		const billingItems: MenuItem[] = [];
		const discountItems: MenuItem[] = [];
		const cartItems: MenuItem[] = [];
		const licenseItems: MenuItem[] = [];

		if (canViewProducts) {
			billingItems.push({
				label: t("navigation.products"),
				url: Paths.products.path,
				template: MenuTemplate,
			});
		}
		if (canViewLimitedCartDiscounts) {
			cartItems.push({
				label: t("navigation.cartDiscount"),
				url: Paths.cartDiscounts.path,
				template: MenuTemplate,
			});
		}

		if (canViewGenerateCartLink) {
			cartItems.push({
				label: t("navigation.cartLinkGenerator"),
				url: Paths.generateCartLink.path,
				template: MenuTemplate,
			});
		}

		if (canViewOrders) {
			billingItems.push({
				label: t("navigation.orders"),
				url: Paths.orders.path,
				template: MenuTemplate,
			});
		}

		if (canViewDiscounts) {
			discountItems.push(
				{
					label: t("navigation.activeDiscounts"),
					url: Paths.activeDiscountActions.path,
					template: MenuTemplate,
				},
				{
					label: t("navigation.archivedDiscounts"),
					url: Paths.archivedDiscountActions.path,
					template: MenuTemplate,
				},
			);
		}

		if (canViewLicenses) {
			licenseItems.push({
				label: t("navigation.licenses"),
				url: Paths.licenses.path,
				template: MenuTemplate,
			});

			licenseItems.push({
				label: t("navigation.licenseDefinitions"),
				url: Paths.licenseDefinitions.path,
				template: MenuTemplate,
			});
		}

		if (canViewExternalLicenses) {
			licenseItems.push({
				label: t("navigation.externalLicensePools"),
				url: Paths.externalLicensePools.path,
				template: MenuTemplate,
			});
		}

		if (billingItems.length > 0) {
			billingItems.push({
				label: t("navigation.discountActions"),
				items: discountItems,
			});

			if (cartItems.length > 0) {
				billingItems.push({
					label: t("navigation.cart"),
					items: cartItems,
				});
			}

			if (licenseItems.length > 0) {
				billingItems.push({
					label: t("navigation.licenses"),
					items: licenseItems,
				});
			}

			menuItems.push({
				label: t("navigation.billing"),
				items: billingItems,
			});
		}

		const customizationItems: MenuItem[] = [];

		if (
			checkPermissionsLocal([
				"UserInAccountPermission_TagAdmin_Read",
				"UserInAccountPermission_TagAdmin_Modify",
			])
		) {
			customizationItems.push({
				label: t("navigation.tags"),
				url: Paths.tags.path,
				template: MenuTemplate,
			});
		}

		if (checkPermissionsLocal(["UserInAccountPermission_Instructors_ReadInstructors"])) {
			customizationItems.push({
				label: t("navigation.instructors"),
				url: Paths.instructors.path,
				template: MenuTemplate,
			});
		}

		if (customizationItems.length > 0) {
			menuItems.push({
				label: t("navigation.customizations"),
				items: customizationItems,
			});
		}

		if (checkPermissionsLocal(["UserInAccountPermission_Email_Templates"])) {
			menuItems.push({
				label: t("navigation.communication"),
				items: [
					{
						label: t("navigation.messages"),
						items: [
							{
								label: t("navigation.messageTemplates"),
								url: Paths.messageDefinitions.path,
								template: MenuTemplate,
							},
							{
								label: t("navigation.sentMessages"),
								url: Paths.messagesDispatches.path,
								template: MenuTemplate,
							},
						],
					},
					{
						label: t("navigation.emailTemplates"),
						url: Paths.emailTemplates.path,
						template: MenuTemplate,
					},
					{
						label: t("navigation.pushTemplates"),
						url: Paths.personalizedNotificationTemplates.path,
						template: MenuTemplate,
					},
				],
			});
		}

		if (checkPermissionsLocal(["UserInAccountPermission_AuthAdmin_Read"])) {
			menuItems.push({
				label: t("navigation.customers"),
				items: [
					{
						label: t("navigation.accounts"),
						url: Paths.accounts.path,
						template: MenuTemplate,
					},
					{
						label: t("navigation.users"),
						url: Paths.users.path,
						template: MenuTemplate,
					},
				],
			});
		}

		if (canViewLevelDefinitions) {
			menuItems.push({
				label: t("navigation.gamification"),
				items: [
					{
						label: t("navigation.level"),
						url: Paths.levelDefinition.path,
						template: MenuTemplate,
					},
				],
			});
		}

		if (checkPermissionsLocal("onlyOwnerOfRoot")) {
			menuItems.push({
				label: t("navigation.files"),
				items: [
					{
						label: t("navigation.offerFiles"),
						url: Paths.offerFiles.path,
						template: MenuTemplate,
					},
					{
						label: t("navigation.allFiles"),
						url: Paths.files.path,
						template: MenuTemplate,
					},
				],
			});
		}
		if (checkPermissionsLocal(["UserInAccountPermission_ReportAdmin_Read"])) {
			menuItems.push({
				label: t("navigation.dsaReports"),
				url: Paths.reports.path,
				template: MenuTemplate,
			});
		}

		const settingsItems: MenuItem[] = [];

		if (
			["staging", "dev"].includes(appEnvironment) &&
			checkPermissionsLocal("onlyOwnerOfRoot")
		) {
			settingsItems.push({
				label: t("navigation.staging"),
				url: Paths.staging.path,
				template: MenuTemplate,
			});
		}

		if (checkPermissionsLocal("onlyOwnerOfRoot") && hasMobileApp) {
			settingsItems.push({
				label: t("navigation.appBuildNumber"),
				url: Paths.appBuildNumber.path,
				template: MenuTemplate,
			});
		}

		if (canViewVideoSources) {
			settingsItems.push({
				label: t("navigation.videoSources"),
				url: Paths.videoSources.path,
				template: MenuTemplate,
			});
		}

		if (checkPermissionsLocal("onlyOwnerOfRoot")) {
			menuItems.push({
				label: t("navigation.settings"),
				items: [
					...settingsItems,
					{
						label: t("navigation.globalSettings"),
						url: Paths.settings.path,
						template: MenuTemplate,
					},
					{
						label: t("navigation.featureToggles"),
						url: Paths.settings.featureToggles.path,
						template: MenuTemplate,
					},
				],
			});
		}

		return menuItems;
	};

	const items = buildMenuItems();

	const shouldGenerateJwt = !!query.Viewer.Auth.currentUser && !hasAppJwt;

	return (
		<BrowserRouter>
			{shouldGenerateJwt && <GenerateAppJwt queryRef={query.Viewer.Auth} />}
			{!!query.Viewer.Auth.currentUser && <PermissionBasedNavigationFeatureToggles />}
			<EnvironmentBadge />
			<div className={"flex align-items-center justify-content-between mr-3"}>
				{isLoggedIn && <AccountSwitcher />}
				<LanguageSwitcher />
			</div>

			{isLoggedIn && (
				<Menubar
					key={`menu-${permissionsKey}`}
					model={items}
					end={
						<Button
							label={t("navigation.logout")}
							onClick={logout}
							className="mr-2"
							icon="pi pi-power-off"
						/>
					}
				/>
			)}
			<Routes>
				{filterByPermission(routes, !!query.Viewer.Auth.currentUser, [
					...(query.Viewer.Auth.currentUser?.permissionsInAccount ?? []),
				]).map((prd) => {
					return <Route key={prd.path} path={prd.path} element={prd.element} />;
				})}

				<Route key={"verboten"} path={"*"} element={<Navigate replace to="/" />} />
			</Routes>
		</BrowserRouter>
	);
}

export function PermissionBasedNavigation() {
	const [queryReference, loadQuery] =
		useQueryLoader<permissionBasedNavigation_Query>(PERMISSION_QUERY);
	const { session } = useAuthContext();
	const [permissionsKey, setPermissionsKey] = useState<string>("");
	const hasAppJwt = useTypedSelector(selectHasAppJwt);
	const appJwt = useTypedSelector(selectAppJwt);

	useEffect(() => {
		// refetch query when TKA token becomes available to get permissions
		loadQuery({}, { fetchPolicy: "store-and-network" });
	}, [loadQuery, session, hasAppJwt, appJwt]);

	// create a key based on session to force re-render when auth state changes
	const sessionKey = session?.user?.id || "no-session";

	return (
		<Suspense fallback={<Loader />}>
			{queryReference && (
				<PermissionBasedNavigationWithPreloadedQuery
					key={`${sessionKey}-${permissionsKey}`}
					queryRef={queryReference}
					onPermissionsChange={setPermissionsKey}
				/>
			)}
		</Suspense>
	);
}

export type RequiredPermissionsType =
	| Permission[]
	| "logged-in"
	| "logged-in-and-logged-out"
	| "only-logged-out"
	| "onlyOwnerOfRoot";
export type HasRequiredPermissionsType = {
	requiredPermissions: RequiredPermissionsType;
};

export function filterByPermission<ItemType extends HasRequiredPermissionsType>(
	items: ItemType[],
	isLoggedIn: boolean,
	permissionsInAccount?: Permission[],
) {
	return items.filter((r) => {
		const loggedOutAndRouteRequiresLoggedOut =
			r.requiredPermissions === "only-logged-out" && !isLoggedIn;
		const loggedInAndRouteOnlyRequiredLoggedIn =
			r.requiredPermissions === "logged-in" && isLoggedIn;
		const loggedInOrLoggedOut = r.requiredPermissions === "logged-in-and-logged-out";

		const permissionsAreKnown = permissionsInAccount && isArray(r.requiredPermissions);
		const isRoot = permissionsInAccount?.includes("AccountPermission_System_Root");
		const noRootPermissionRequiredAndUserIsOwner =
			!r.requiredPermissions.includes("AccountPermission_System_Root") &&
			permissionsInAccount?.includes("AccountPermission_System_Root");
		const isLoggedInAndHasAllNecessaryPermissions =
			permissionsAreKnown &&
			(_.difference(r.requiredPermissions, permissionsInAccount).length === 0 ||
				isRoot ||
				noRootPermissionRequiredAndUserIsOwner);

		if (r.requiredPermissions === "onlyOwnerOfRoot") return isRoot;

		return (
			loggedInOrLoggedOut ||
			loggedOutAndRouteRequiresLoggedOut ||
			loggedInAndRouteOnlyRequiredLoggedIn ||
			isLoggedInAndHasAllNecessaryPermissions
		);
	});
}
